import React, { useCallback } from 'react'

import clsx from 'clsx'

import BuildConfig from '$extensionSrc/buildConfig'
import ActionButton, {
  ActionButtonIcon,
  ActionButtonSize,
  ActionButtonType,
} from '$extensionSrc/components/ActionButton'
import {
  addAuthenticationMethodPath,
  extensionRedirectPath,
  googleOauthPath,
} from '$extensionSrc/consts/urlConsts'
import AddEmailIcon from '$extensionSrc/icons/add-email.svg'
import BlockPartyCelebrateIcon from '$extensionSrc/icons/block-party-celebrate.svg'
import MyDataIcon from '$extensionSrc/icons/my-data.svg'

import ContinueWithGoogle from './ContinueWithGoogle'
import styles from './EnterprisePlanWelcome.module.scss'

export function EnterprisePlanWelcomeRow({
  Icon,
  title,
  children,
}: React.PropsWithChildren<{
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  title: string
}>) {
  return (
    <div className={styles.EnterprisePlanWelcomeRow}>
      <div className={styles.iconCircle}>
        <Icon aria-hidden className={styles.icon} />
      </div>
      <div className={styles.rowContent}>
        <h2 className={clsx('heading2', 'bold', styles.title)}>{title}</h2>
        <div className={clsx(styles.rowItems, 'body2')}>{children}</div>
      </div>
    </div>
  )
}

export default function EnterprisePlanWelcome({
  organizationName,
  nextUrl,
}: {
  organizationName: string
  nextUrl: string | null
}) {
  const handleClickContinue = useCallback(() => {
    window.location.assign(nextUrl ?? extensionRedirectPath)
  }, [nextUrl])

  const handleClickAddEmail = useCallback(() => {
    const urlParams = new URLSearchParams()
    urlParams.append('next', extensionRedirectPath)
    window.location.assign(
      `${addAuthenticationMethodPath}?${urlParams.toString()}`,
    )
  }, [])

  const addAuthOauthParams = new URLSearchParams()
  addAuthOauthParams.append('next', extensionRedirectPath)
  addAuthOauthParams.append('action', 'addAuthenticationMethod')
  const addAuthOauthPath = `${googleOauthPath}?${addAuthOauthParams.toString()}`

  return (
    <div className={styles.EnterprisePlanWelcome}>
      <h1 className={clsx('heading1', 'bold')}>
        Your {BuildConfig.TENANT_NAME} access starts now!
      </h1>
      <div className={styles.mainContent}>
        <div className={styles.contentPreface}>
          <p>
            {`Great news! You now have full access to ${BuildConfig.TENANT_NAME}, 
                        courtesy of ${organizationName}. To start, here are two 
                        helpful things to know:`}
          </p>
          <BlockPartyCelebrateIcon
            aria-hidden
            className={styles.celebrateIcon}
          />
        </div>
        <EnterprisePlanWelcomeRow
          Icon={MyDataIcon}
          title="Your data will always be your own"
        >
          <p>Your employer will not have access to:</p>
          <ul>
            <li>your social account login information</li>
            <li>your social account usernames</li>
            <li>your social account content</li>
          </ul>
        </EnterprisePlanWelcomeRow>
        <EnterprisePlanWelcomeRow
          Icon={AddEmailIcon}
          title="You can link your account to a personal email"
        >
          <p>
            If you'd also like to log in with a personal email address, you can
            link it here now (or later in your account settings).
          </p>
          <div className={styles.linkEmail}>
            <span className="body2 bold">Link a new login email address:</span>
            <ContinueWithGoogle redirectUrl={addAuthOauthPath} iconOnly />
            <ActionButton
              aria-label="Add email"
              type={ActionButtonType.SECONDARY}
              size={ActionButtonSize.LARGE}
              iconOnly
              iconName={ActionButtonIcon.EMAIL}
              onClick={handleClickAddEmail}
              className={clsx(
                styles.addEmailButton,
                styles.override,
                styles.paddingOverride,
              )}
            />
          </div>
        </EnterprisePlanWelcomeRow>
      </div>
      <ActionButton
        aria-label="Continue"
        type={ActionButtonType.PRIMARY}
        onClick={handleClickContinue}
        size={ActionButtonSize.LARGE}
        className={styles.continueButton}
      >
        Continue
      </ActionButton>
    </div>
  )
}
